import useElementHeight from '@app/hooks/useElementHeight';
import { BlockContent } from '@app/sanity.types';
import { Button, Space, Typography } from 'antd';
import { Minimize2 } from 'lucide-react';
import { PortableText } from 'next-sanity';
import { useState } from 'react';
import styles from './styles.module.scss';

type Props = {
  description: BlockContent;
};

/**
 * Height the description is clipped to while collapsed. Applied inline rather than from the
 * stylesheet so the same number decides both the clipping and whether the toggle is shown — when
 * the two were separate they could disagree, which is how long descriptions ended up clipped with
 * no way to expand them.
 */
const COLLAPSED_MAX_HEIGHT = 260;

const { Title } = Typography;

const BoatDescription = ({ description }: Props) => {
  // Measures the full, unclipped content: the ref sits on a wrapper inside the clipping element,
  // so `overflow: hidden` on the parent does not shrink it. Previously this ref was passed to
  // every rendered paragraph, and since a ref holds a single node it ended up on the *last* one —
  // so a long description made of short paragraphs measured only its final paragraph, fell under
  // the threshold, and rendered no toggle even though the text was cut off.
  const [contentRef, contentHeight] = useElementHeight();

  const [isExpanded, setIsExpanded] = useState(false);

  const toggleExpand = () => setIsExpanded((prev) => !prev);

  const isTruncatable = contentHeight > COLLAPSED_MAX_HEIGHT;

  return (
    <div className={styles.container}>
      <Space orientation="vertical" size={16} className={styles.wrapDesc}>
        <Title level={2} className={styles.descTitle}>
          Boat Description
        </Title>

        <div className={styles.description} style={{ maxHeight: isExpanded ? undefined : COLLAPSED_MAX_HEIGHT }}>
          <div ref={contentRef}>
            <PortableText
              value={description}
              components={{
                block: {
                  normal: ({ children }) => <p className={styles.paragraph}>{children}</p>,
                },
              }}
            />
          </div>

          {isTruncatable ? (
            isExpanded ? (
              <Button type="link" onClick={toggleExpand} className={styles.moreCollapsed}>
                <Minimize2 size={16} /> Collapse
              </Button>
            ) : (
              <Button type="link" onClick={toggleExpand} className={styles.readMore}>
                ...Read more
              </Button>
            )
          ) : null}
        </div>
      </Space>
    </div>
  );
};

export default BoatDescription;
